import { useAuth } from "@dl/web-core-js/auth";
import { PageHeader } from "@dl/web-core-js/shell";
import { EditPane, type EditPaneTab } from "@dl/web-core-js/ui";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { NexpimView } from "../../views";
import { OperatorGrid, ScrapReasonGrid, StorageLocationGrid } from "./manufacturingConfigPages";

/**
 * The shop floor's reference lists, on one page: who scans in at a kiosk, why
 * parts are scrapped, and where finished goods wait. Each tab is an inline-edited grid like every config screen. The
 * floor's single-row settings (refresh, documents, packing line) sit on the
 * Settings page with every other setting.
 */
export function ShopFloorSetupPage() {
  const { t } = useTranslation();
  const { hasView } = useAuth();
  const title = t("navigation.item.shop-floor-setup", "Shop floor setup");

  if (!hasView(NexpimView.CONFIG_SHOP_FLOOR)) {
    return (
      <>
        <PageHeader title={title} />
        <div className="p-4 lg:p-6">
          <p className="text-[13px] text-ink-muted">
            {t("error.access-denied.detail", "Your account does not have permission to view this page.")}
          </p>
        </div>
      </>
    );
  }

  const tabs: EditPaneTab[] = [
    { id: "operators", flush: true, label: t("shop-floor-setup.operators", "Operators"), content: <OperatorGrid /> },
    { id: "scrap-reasons", flush: true, label: t("shop-floor-setup.scrap-reasons", "Scrap reasons"), content: <ScrapReasonGrid /> },
    { id: "locations", flush: true, label: t("shop-floor-setup.locations", "Storage locations"), content: <StorageLocationGrid /> },
  ];

  return (
    <>
      <PageHeader
        title={title}
        subtitle={
          <span>
            {t("shop-floor-setup.subtitle.v2", "Badges, scrap reasons and finished goods locations.")}{" "}
            {hasView(NexpimView.CONFIG_SETTINGS) && (
              <Link to="/config/settings" className="text-accent underline-offset-2 hover:underline">
                {t("shop-floor-setup.settings-link", "Refresh, documents and packing line are in Settings.")}
              </Link>
            )}
          </span>
        }
      />
      <EditPane tabs={tabs} urlParam="tab" />
    </>
  );
}
